import {useEffect, useState} from "react";
import {IDemandWLGenerator, initialDemandWLGeneratorObj, IValidationErrors} from "./demandWLGenerator.interface";
import {useMutation, useQuery} from "react-query";
import {getObjectData, postObjectData} from "../../../actions/getObjectData";
import {
    baseSiteUrl,
    demandBaseUrl,
    demandWLGeneratorBaseUrl,
    integrationBaseUrl,
    runLogsBaseUrl,
    sellersBaseUrl
} from "../../../config/generals";
import {AlertTypes, apiClient, IAlertData, IGetAllParams, setAlert} from "@leap4/frontend";
import {IDemand} from "../demands/demands/demand.interface";
import {demandWLGeneratorContext} from "./base/demandWLGeneratorContext";
import ReportTab from "./base/reportTab";
import {ISeller} from "../sellers/seller.interface";
import {ISite} from "../sites/site.interface";
//import _ from "lodash";
import {RootState} from "../../../interfaces/root-state.interface";
import {connect} from "react-redux";
import {updateObjectById} from "../../../actions/creatingObjectsFunc";
import {deleteItem} from "../../../actions/deleteObjects";
import {useLocation} from "react-router-dom";


const WLGeneratorManager = ({ auth, setAlert }: { auth: any, setAlert: (alert: IAlertData) => void }) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const urlDemandWLId = queryParams.get('id');

    const getAllDemands = async (): Promise<IDemand[]> => {
        const fields = {
            _id: 1,
            name: 1,
            AdvertisingSystem: 1,
            PubAccId: 1,
        };
        const getParams: IGetAllParams = {
            fields,
            filter: {
                filter: {
                    isDeleted: 0,
                    isActive: 1,
                },
            },
        };
        const demandData = await getObjectData(demandBaseUrl, getParams)
        return demandData.results;
    };
    const getPublishers = async () => {
        const params = {
            pageSize: 1,
            page: 1,
            sort: { created: -1 },
        }
        // get last seller json
        const response = await getObjectData(sellersBaseUrl, params);
        return response.results[0].sellers as ISeller[];

    }

    const validateFields = (isForSaving = false) => {
        let errors = {} as IValidationErrors;
        if (isForSaving && !demandWL.templateName) errors.templateName = 'Template name is required';
        if (demandWL.includeSiteDomainAdsTxtLine && demandWL.demandPartnerDomain.domain &&
            (demandWL.dimensions.length !== 1 || demandWL.dimensions[0].key !== 'AppIdentifier')) {
            errors.dimensions = 'Only AppIdentifier is allowed when include SiteDomainAdsTxtLine and demand is selected. please remove other dimensions';
        } else if (demandWL.dimensions.length === 0) {
            errors.dimensions = 'At least one dimension is required';
        }
        console.log('errors', errors);
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const getReportColumns = (docs: any) => {
        const columns = []
        for (const column of Object.keys(docs[0])) {
            columns.push({name: column, label: column, options: {filter: true, sort: true}})
        }
        return columns;
    }

    const runReport = async (demandWLObj = demandWL) => {
        demandWLObj.manuallyAddedBundles = demandWL.manuallyAddedBundles || [];
        demandWLObj.manuallyRemovedBundles = demandWL.manuallyRemovedBundles || [];

        let maxAttempts = 30;
        const pollInterval = 3000; // waiting maximum 1.5 min
        if (!validateFields(false)) {
            return { reportData: [], reportColumns: [], error: 'Validation error' };
        }
        try {
            const reportKey = await postObjectData(demandWLGeneratorBaseUrl + '/getReport', demandWLObj);
            let status;
            do {
                const response = await postObjectData(demandWLGeneratorBaseUrl + '/getStatus', {reportKey});
                status = response.status;
                if(status === 'completed'){
                    const reportData = response.data;
                    const columns = getReportColumns(reportData);
                    const manuallyAddedBundlesEntries = demandWLObj.manuallyAddedBundles.map(bundle => ({
                        ...reportData[0],
                        AppIdentifier: bundle.bundleId
                    }))
                    const updatedDocs = [...reportData, ...manuallyAddedBundlesEntries]
                    return { reportData: updatedDocs, reportColumns: columns }
                }
                if(status === 'failed'){
                    return { reportData: [], reportColumns: [], error: 'Error running report' };
                }
                await new Promise((resolve) => setTimeout(resolve, pollInterval));

            } while (maxAttempts--)
        return { reportData: [], reportColumns: [], error: 'Error running report' };
        }catch(error){
            console.log('error',error);
            return { reportData: [], reportColumns: [], error: 'Error running report' };
        }
    }

    const getSiteData = async () => {
        const params = {
            pageSize: 1,
            page: 1,
            sort: { created: -1 },
        }
        // get last seller json
        const response = await getObjectData(baseSiteUrl, params);
        return response.results[0] as ISite;
    }

    const saveOrUpdateWLGeneratorTemplate = async (params:{id:string,data:any,ignoreValidation:boolean}) => {
        const { id, data, ignoreValidation } = params;
        if (!ignoreValidation && !validateFields(true)) {
            return { error: 'Validation error' };
        }
        if(!id){
            demandWL.createBy = auth.user.name;
            await postObjectData(demandWLGeneratorBaseUrl, data);
            setAlert({ msg: 'Template saved', type: AlertTypes.SUCCESS });
        }
        else{
            await updateObjectById(id,data,demandWLGeneratorBaseUrl);
            setAlert({ msg: 'Template updated', type: AlertTypes.SUCCESS });
        }

        await refatchReportTamplate();
        return true;
    }
    const getAllWLGeneratorTemplates = async (): Promise<IDemandWLGenerator[]> => {
        const response = await getObjectData(demandWLGeneratorBaseUrl, { populate: ['bridges'] });
        return response.results;
    }
    const deleteReport = async (reportId: string) => {
        try{
            const response = await deleteItem(demandWLGeneratorBaseUrl, reportId);
            await refatchReportTamplate();
            setAlert({ msg: "Template deleted successfully", type: AlertTypes.SUCCESS });
            return response;
        }catch(error){
            setAlert({ msg: "Error deleting template", type: AlertTypes.FAILURE });
            return null;
        }
    }

    const getActiveIntegrations = async () => {
        const fields = {
            _id: 1,
            name: 1,
            adserver: 1,
        };
        const getParams: IGetAllParams = {
            fields,
            filter: {
                filter: {
                    isDeleted: 0,
                    isActive: 1,
                },
            },
        };
        const integrationData = await getObjectData(integrationBaseUrl, getParams);
        return integrationData.results;
    };

    const downloadRunLogCsv = async (id: string) => {
        const response = await apiClient.get(`${runLogsBaseUrl}/${id}/csv`, {
            headers: {
                'Accept': 'text/csv, application/json',
                'Content-Type': 'application/json',
            }
        });
        return response.data;
    };

    const downloadBulkRunLogsCsv = async (ids: string[]) => {
        const response = await apiClient.post(`${runLogsBaseUrl}/bulk/csv`, {
            ids: ids
        }, {
            responseType: 'blob',
            headers: {
                'Accept': 'text/csv, application/json',
                'Content-Type': 'application/json',
            }
        });
        return response.data;
    };

    const [demandWL, setDemandWL] = useState<IDemandWLGenerator>(initialDemandWLGeneratorObj);
    const [tabIndex, setTabIndex] = useState(0);
    const [validationErrors, setValidationErrors] = useState({} as IValidationErrors);
    const {data: allDemands} = useQuery('allDemands', getAllDemands);
    const {data: reportTemplates,refetch:refatchReportTamplate } = useQuery('reportTemplates', getAllWLGeneratorTemplates);
    const {data: reportData, mutate: mutateReport , isLoading: isLoadingReport}= useMutation('reportData', runReport );
    const {mutate: saveOrUpdateReportTemplate} = useMutation('saveReportTemplate', saveOrUpdateWLGeneratorTemplate);
    const {mutate: deleteReportTemplate } = useMutation('deleteReportTemplate', deleteReport);
    const {data: publishers} = useQuery('publishers', getPublishers);
    const {data: siteData  }= useQuery('siteData', getSiteData);
    const {data: activeIntegrations} = useQuery('activeIntegrations', getActiveIntegrations);
    // const {mutate: downloadCsv} = useMutation('downloadCsv', downloadRunLogCsv);
    // const {mutate: downloadBulkCsv} = useMutation('downloadBulkCsv', downloadBulkRunLogsCsv);

    useEffect(() => {
        setValidationErrors({} as IValidationErrors);
    },[tabIndex]);

    useEffect(() => {
        if (urlDemandWLId && reportTemplates) {
            const template = reportTemplates.find(template => template._id === urlDemandWLId);
            if (template) {
                setDemandWL(template);
                setTabIndex(0); // Switch to Report Builder tab
            }
        }
    }, [urlDemandWLId, reportTemplates]);

    if(!allDemands || !publishers || !siteData) return <div>Loading...</div>
    demandWL.siteDomain.domain = siteData.domain;


    return (
        <demandWLGeneratorContext.Provider
            value={{
                demandWLGenerator: demandWL,
                setDemandWLGenerator: setDemandWL,
                demand: {} as IDemand,
                allDemands: allDemands || [],
                runReport:mutateReport,
                publishers: publishers || [],
                reportData: reportData?.reportData,
                reportColumns: reportData?.reportColumns||[],
                isLoadingReport,
                validationErrors,
                setTabIndex,
                tabIndex,
                updateReportTemplate: (reportId)=>{
                    const reportTemplate=reportTemplates.find((template)=>template._id===reportId);
                    setTabIndex(0);
                    if(reportTemplate) setDemandWL(reportTemplate)
                },
                deleteReportTemplate: (reportId)=>{
                    deleteReportTemplate(reportId);

                },
                runReportTemplate: (reportId)=>{
                    const reportTemplate=reportTemplates.find((template)=>template._id===reportId);
                    if(reportTemplate) {
                        setDemandWL(reportTemplate);
                    }
                    setTabIndex(0)
                    mutateReport(reportTemplate);
                },
                reportTemplates: reportTemplates || [],
                saveOrUpdateWLGeneratorTemplate: saveOrUpdateReportTemplate,
                integrations: activeIntegrations || [],
                downloadCsv: (id: string) => downloadRunLogCsv(id),
                downloadBulkCsv: (ids: string[]) => downloadBulkRunLogsCsv(ids),
                setAlert
            }}
        >
            <ReportTab clearReport={()=>setDemandWL(initialDemandWLGeneratorObj)} setAlert={setAlert}/>
        </demandWLGeneratorContext.Provider>
    );
}

// Map Redux state to component props
const mapStateToProps = (state: RootState) => ({
    auth: state.auth,
});

const mapDispatchToProps = {
    setAlert
};
// Connect Redux to the component
export default connect(mapStateToProps, mapDispatchToProps)(WLGeneratorManager);
